/**
 * Source Documents Section for Accountant Report View
 * Displays downloadable financial documents (Tax Returns, P&L, Supporting Docs)
 */

import React from 'react';
import { FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface DocumentFile {
  id?: string;
  filename?: string;
  url?: string;
}

interface SourceDocumentsSectionProps {
  reportId: string;
  clientName?: string;
  tax1065Files?: DocumentFile[];
  plFiles?: DocumentFile[];
  uploadedDocuments?: DocumentFile[];
  getAuthHeader: () => { Authorization: string } | {};
}

// Reusable download handler. Uses plain `fetch` (not the project's
// `apiFetch` helper) because `apiFetch` returns already-parsed data —
// calling `.blob()` on its return value fails at runtime since the
// return is a Blob/JSON/string, not a Response. The canonical
// blob-download pattern in this codebase (see EvidenceChecklist.tsx,
// wizard/Step6.tsx, TransactionViewer.tsx) uses raw `fetch` so the
// Response object is preserved and `.blob()` works.
//
// 2026-04-26 archaeology: this function was previously written against
// `apiFetch` and was broken at runtime; the broken-twin
// `handleDownloadAll` also referenced an undefined `api` (TS-FU1 in
// audit/follow-ups.md). Both are now restored to the project's
// canonical fetch-based download pattern.
const handleDownload = async (
  url: string,
  filename: string,
  getAuthHeader: () => { Authorization: string } | {}
) => {
  try {
    const response = await fetch(url, { headers: getAuthHeader() });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
  } catch (err) {
    console.error('Download failed:', err);
  }
};

// Document list component
interface DocumentListProps {
  files: DocumentFile[];
  reportId: string;
  docType: 'tax' | 'pl' | 'doc';
  colorStyle: React.CSSProperties;
  emptyMessage: string;
  getAuthHeader: () => { Authorization: string } | {};
}

function DocumentList({ files, reportId, docType, colorStyle, emptyMessage, getAuthHeader }: DocumentListProps) {
  if (!files || files.length === 0) {
    return <p className="text-sm italic" style={{ color: 'var(--color-text-muted)' }}>{emptyMessage}</p>;
  }

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {files.map((file, idx) => {
        const downloadUrl = file.url || `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/documents/${docType}/${file.id || file.filename}`;
        const displayName = file.filename || `Document ${idx + 1}`;

        return (
          <a
            key={idx}
            href={downloadUrl}
            download={displayName}
            className="flex items-center justify-between text-sm p-2 rounded transition-colors cursor-pointer hover:bg-[var(--color-bg-hover)]"
            style={colorStyle}
            onClick={(e) => {
              e.preventDefault();
              handleDownload(downloadUrl, displayName, getAuthHeader);
            }}
          >
            <span className="truncate">{displayName}</span>
            <svg className="w-4 h-4 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>
        );
      })}
    </div>
  );
}

export default function SourceDocumentsSection({
  reportId,
  clientName,
  tax1065Files = [],
  plFiles = [],
  uploadedDocuments = [],
  getAuthHeader
}: SourceDocumentsSectionProps) {
  const handleDownloadAll = async () => {
    try {
      const downloadUrl = `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/documents/download-all`;
      const response = await fetch(downloadUrl, { headers: getAuthHeader() });
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientName || 'Report'}_Documents_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download all documents:', error);
    }
  };

  return (
    <div className="rounded-md p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <h2 className="cs-h2 mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
        <FileText size={22} className="mr-2" />
        Source Financial Documents
      </h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        All original documents uploaded and used in this calculation. Essential for court admissibility and forensic review.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Tax Returns */}
        <div className="rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 mr-2" style={{ color: 'var(--color-needs-human-text)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Tax Returns (1065 & K-1)</h3>
          </div>
          <DocumentList
            files={tax1065Files}
            reportId={reportId}
            docType="tax"
            colorStyle={{ color: 'var(--color-text-primary)' }}
            emptyMessage="No tax documents uploaded"
            getAuthHeader={getAuthHeader}
          />
        </div>

        {/* P&L Statements */}
        <div className="rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 mr-2" style={{ color: 'var(--color-needs-human-text)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>P&L Statements</h3>
          </div>
          <DocumentList
            files={plFiles}
            reportId={reportId}
            docType="pl"
            colorStyle={{ color: 'var(--color-text-primary)' }}
            emptyMessage="No P&L documents uploaded"
            getAuthHeader={getAuthHeader}
          />
          {plFiles && plFiles.length > 5 && (
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>Showing {plFiles.length} files</p>
          )}
        </div>

        {/* Supporting Documents */}
        <div className="rounded-md p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 mr-2" style={{ color: 'var(--color-needs-human-text)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Supporting Docs</h3>
          </div>
          <DocumentList
            files={uploadedDocuments}
            reportId={reportId}
            docType="doc"
            colorStyle={{ color: 'var(--color-text-primary)' }}
            emptyMessage="No supporting documents"
            getAuthHeader={getAuthHeader}
          />
        </div>
      </div>

      <div className="mt-6 flex gap-4">
        <button
          onClick={handleDownloadAll}
          className="flex-1 px-4 py-3 rounded-md transition-all font-medium flex items-center justify-center gap-2 cursor-pointer"
          style={{ backgroundColor: 'transparent', color: 'var(--color-brand)', border: '1px solid var(--color-border)' }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download All Documents (ZIP)
        </button>
      </div>
    </div>
  );
}
